import { HttpClient } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { environment } from '../../environments/environment';
import { ContentApiService } from '@core/services/api/content-api.service';
import { TranslocoLoaderData } from '@jsverse/transloco/lib/transloco.loader';
import { registerLocaleData } from '@angular/common';
import { catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import localeTr from '@angular/common/locales/tr';

import {
  provideTranslocoLocale,
  TranslocoLocaleModule,
} from '@jsverse/transloco-locale';

import {
  Translation,
  TranslocoLoader,
  TranslocoModule,
  getBrowserLang,
  provideTransloco,
} from '@jsverse/transloco';

const AVAILABLE_LOCALE_SETTINGS = {
  de: localeDe,
  en: localeEn,
  fr: localeFr,
  es: localeEs,
  pt: localePt,
  tr: localeTr,
};

Object.values(AVAILABLE_LOCALE_SETTINGS).forEach(registerLocaleData);

export const DEFAULT_LANGUAGE = getBrowserLang() || 'de-local';
export const FALLBACK_LANGUAGE = 'de-local';
export const KEYS_LANGUAGE = 'keys';

export const AVAILABLE_LANGUAGES = [
  // KEYS_LANGUAGE,
  FALLBACK_LANGUAGE,
  'en-US',
  'de-DE',
];

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  constructor(
    private http: HttpClient,
    private contentApiService: ContentApiService,
  ) {}

  getTranslation(
    language: string,
    data?: TranslocoLoaderData,
  ): Observable<Translation> {
    // Clean the language parameter by removing leading slash
    const cleanLanguage = language.startsWith('/')
      ? language.substring(1)
      : language;

    if (cleanLanguage.endsWith('-local')) {
      return this.http.get<Translation>(`./assets/i18n/${cleanLanguage}.json`);
    } else if (cleanLanguage === KEYS_LANGUAGE) {
      return of({});
    } else {
      if (!AVAILABLE_LANGUAGES.includes(cleanLanguage)) {
        AVAILABLE_LANGUAGES.push(cleanLanguage);
      }
      return this.contentApiService.getTranslation(cleanLanguage).pipe(
        catchError((err) => {
          console.error(err);
          console.error(
            `"${cleanLanguage}" translations cannot be loaded. Falling back to local translations.`,
          );

          // Fallback to local file if API fails
          const localLanguage = `${cleanLanguage}-local`;
          return this.http
            .get<Translation>(`./assets/i18n/${localLanguage}.json`)
            .pipe(
              catchError(() => {
                console.error(
                  `Local fallback for ${localLanguage} also failed`,
                );
                return of({});
              }),
            );
        }),
      );
    }
  }
}

@NgModule({
  exports: [TranslocoModule, TranslocoLocaleModule],
  providers: [
    provideTransloco({
      config: {
        availableLangs: AVAILABLE_LANGUAGES,
        defaultLang: DEFAULT_LANGUAGE,
        fallbackLang: FALLBACK_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: environment.production,
        missingHandler: {
          useFallbackTranslation: true,
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoLocale({
      langToLocaleMapping: {
        'de-local': 'de-DE',
        en: 'en-US',
        de: 'de-DE',
      },
    }),
  ],
})
export class TranslocoRootModule {}
